import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';
import { useEffectOnce } from 'react-use';
import createError from 'http-errors';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import queryString from 'query-string';

import {
  getMerchantGroup,
  getMerchantGroupFailure,
  getMerchantGroupTheme,
} from '@artemis/store/merchantGroup/selectors';
import { getIpData } from '@artemis/store/ipData/selectors';
import NavBar from '@artemis/components/NavBar';
import { useFormatMessage } from '@artemis/integrations/contentful/utils';
import useDerivedTheme from '@artemis/utils/useDerivedTheme';
import usePersistedQuery from '@artemis/utils/query/usePersistedQuery';
import { page } from '@artemis/utils/analytics';
import AccountControls from '@artemis/containers/AccountControls';
import MerchantLocationList from '@artemis/components/MerchantLocationList';
import {
  getDistanceUnitForCountry,
  DISTANCE_UNIT,
} from '@artemis/utils/location';
import useTrackWebVitals, { PAGE_NAME } from '@artemis/utils/useTrackWebVitals';
import Metadata from '@artemis/components/Metadata';
import { hasActiveOrders } from '@artemis/store/order/selectors';
import useLoadActiveOrders from '@artemis/containers/OrderBar/useLoadActiveOrders';
import {
  MERCHANT_GROUP_PARAM,
  MERCHANT_GROUP_REFERER_PARAM,
} from '@artemis/utils/query/constants';
import LocationControl from './LocationControl';
import { logImpression } from './analytics';

const OrderBarBanner = dynamic(() =>
  import('@artemis/containers/OrderBar/OrderBarBanner'),
);

const LocationsContainer = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 1114px;
  padding: 0 10px;
  box-sizing: border-box;
`;

const GroupName = styled.h1`
  font-weight: ${props => props.theme.typography.fontWeightBold};
  text-align: center;
`;

const Icon = styled.img`
  margin: 0 auto;
  display: block;
  padding-bottom: 16px;
  width: 80px;
  height: 80px;
`;

const TitleDiv = styled.div`
  margin: auto;
  margin-bottom: 60px;
  margin-top: 32px;
  max-width: 521px;
`;

const LocationList = styled(MerchantLocationList)`
  max-width: 700px;
  margin: auto;
`;

const FlexPadding = styled.div`
  flex: 1;
`;

const measure = (lat1, lon1, lat2, lon2, unit = DISTANCE_UNIT.KM) => {
  const R = 6378.137; // Radius of earth in KM
  const dLat = (lat2 * Math.PI) / 180 - (lat1 * Math.PI) / 180;
  const dLon = (lon2 * Math.PI) / 180 - (lon1 * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return unit === DISTANCE_UNIT.KM ? d : d * (5 / 8);
};

const LocationsPage = () => {
  const router = useRouter();
  const persistedQuery = usePersistedQuery({
    // Take the group from path params and put it in the menu page url's query params
    replace: { [MERCHANT_GROUP_PARAM]: MERCHANT_GROUP_REFERER_PARAM },
  });
  useTrackWebVitals(PAGE_NAME.LOCATION);
  useLoadActiveOrders();

  const groupTheme = useSelector(getMerchantGroupTheme);
  const failure = useSelector(getMerchantGroupFailure);
  const ipData = useSelector(getIpData);
  const merchantGroup = useSelector(getMerchantGroup);
  const showOrderBar = useSelector(hasActiveOrders);

  const derivedTheme = useDerivedTheme([groupTheme]);
  const title = useFormatMessage({ entry: 'brand.html.title' });
  const merchantIconLabel = useFormatMessage({ entry: 'brand.icon' });

  const initialLatLng = ipData
    ? { lat: ipData.latitude, lng: ipData.longitude }
    : null;

  const [merchantGroupSorted, setMerchantGroupSorted] = useState([]);
  const [userLatLng, setUserLatLng] = useState(initialLatLng);

  // eslint-disable-next-line camelcase
  const unit = getDistanceUnitForCountry({ countryCode: ipData?.country_code });
  const brandTitle = derivedTheme.landingHeaderText;

  const buildMenuUrl = merchant =>
    merchant.url.replace('/menu', '/order') + persistedQuery;

  const updateMerchants = () => {
    let merchants = [...merchantGroup];

    merchants = merchants.map(item => {
      const newItem = { ...item };
      const { latitude, longitude } = item.latLng;

      newItem.url = buildMenuUrl(item);

      if (userLatLng) {
        newItem.distance = {
          value: measure(
            latitude,
            longitude,
            userLatLng.lat,
            userLatLng.lng,
            unit,
          ),
          unit,
        };
      }

      return newItem;
    });

    if (userLatLng) {
      merchants = merchants.sort((a, b) => a.distance.value - b.distance.value);
    }

    setMerchantGroupSorted(merchants);
  };

  const onLocationChange = data => {
    setUserLatLng(data);
  };

  useEffectOnce(() => {
    if (merchantGroup.length === 1) {
      window.location.replace(buildMenuUrl(merchantGroup[0]));
    } else {
      logImpression(router.query[MERCHANT_GROUP_PARAM], merchantGroup);
      page({
        MERCHANT_GROUP_ID: router.query[MERCHANT_GROUP_PARAM],
      });
    }
  }, [merchantGroup]);

  useEffect(updateMerchants, [merchantGroup, userLatLng]);

  const pageUrl = `https://ritual.co${router.asPath}`;
  const pageTitle = brandTitle || false;
  const pageDescription = useFormatMessage({ entry: 'brand.html.description' });
  const pageImage = derivedTheme.iconUrl || false;

  if (failure) {
    throw new createError.NotFound();
  }

  if (merchantGroup.length === 1) {
    return null;
  }
  const openGraphTwitterTags = {
    url: pageUrl,
    siteName: 'Ritual',
    ...(pageTitle && { title: pageTitle }),
    ...(pageDescription && { description: pageDescription }),
    ...(pageImage && {
      image: {
        url: pageImage,
      },
      card: 'summary_large_image',
    }),
  };

  return (
    <>
      <Metadata
        title={title}
        canonical={queryString.parseUrl(pageUrl).url}
        description={pageDescription}
        openGraph={openGraphTwitterTags}
        twitter={openGraphTwitterTags}
      />
      <ThemeProvider theme={derivedTheme}>
        <NavBar>
          <FlexPadding />
          <AccountControls />
        </NavBar>
        {showOrderBar && <OrderBarBanner />}
        <LocationsContainer>
          <TitleDiv>
            {derivedTheme.iconUrl && (
              <Icon
                src={derivedTheme.iconUrl}
                width="80"
                height="80"
                alt={merchantIconLabel}
              />
            )}
            {brandTitle && <GroupName>{brandTitle}</GroupName>}
          </TitleDiv>

          <LocationControl onLocationChange={onLocationChange} />

          {merchantGroupSorted.length > 0 && (
            <LocationList merchants={merchantGroupSorted} />
          )}
        </LocationsContainer>
      </ThemeProvider>
    </>
  );
};

LocationsPage.propTypes = {};

LocationsPage.defaultProps = {};

export default LocationsPage;
